import React, { useCallback, useEffect } from "react";
import { Input } from "antd";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Box from "./box";

/**
 * Renders a search input component.
 *
 * @param {object} props - An object containing the function props.
 * @param {function} props.setSearchString - Function to update the search string.
 * @returns {JSX.Element} The search component element.
 */
export function Search({
  setSearchString,
  allowClear = false,
  box = true,
}: {
  setSearchString: (searchString: string) => void;
  allowClear?: boolean;
  box?: boolean;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("search") || "";

  useEffect(() => {
    setSearchString(query);
  }, [query, setSearchString]);

  const searchStringHandler = useCallback(
    (value: string | undefined) => {
      // If value is undefined, set it to an empty string
      const searchValue = value ?? "";

      setSearchString(searchValue);
      const newParams: { [key: string]: string } = {};

      // Retain pageSize if it exists
      const currentPageSize = searchParams.get("pageSize");
      if (currentPageSize) {
        newParams.pageSize = currentPageSize;
      }

      // Handle page: reset to 1 if search changes and page is not 1
      const currentPage = searchParams.get("page");
      if (searchValue !== query && currentPage !== null) {
        newParams.page = "1";
      } else if (currentPage) {
        newParams.page = currentPage;
      }

      if (searchValue) {
        newParams.search = searchValue;
      }

      setSearchParams(newParams);
    },
    [setSearchParams, setSearchString, searchParams, query],
  );

  const searchInput = (
    <Input.Search
      onSearch={searchStringHandler}
      size="large"
      placeholder={t("translation:search")}
      defaultValue={query}
      allowClear={allowClear}
    />
  );

  return box ? <Box>{searchInput}</Box> : searchInput;
}
